import { planzoneSettings, projectDetailsConfiguration } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRetrieveProjectDetailsConfigurationRequest(
  state: State,
  planzone: Boundaries["retrieveProjectDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToPending(),
          configuration: projectDetailsConfiguration,
        },
      ];
}

function handleRetrieveProjectDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["retrieveProjectDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleRetrieveProjectDetailsConfigurationFailure(
  state: State,
  planzone: Boundaries["retrieveProjectDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToFailure(),
          configuration: projectDetailsConfiguration,
        },
      ];
}

function handleEditProjectDetailsConfigurationRequest(
  state: State,
  {
    planzone,
    nextConfiguration,
  }: Boundaries["editProjectDetailsConfiguration"]["request"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration: nextConfiguration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration: nextConfiguration,
        },
      ];
}

function handleEditProjectDetailsConfigurationSuccess(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectDetailsConfiguration"]["success"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

function handleEditProjectDetailsConfigurationFailure(
  state: State,
  {
    planzone,
    configuration,
  }: Boundaries["editProjectDetailsConfiguration"]["failure"],
): State {
  const index = state.findIndex((req) => req.planzone.uuid === planzone.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], configuration },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          planzone,
          request: setRequestStateToSuccess(),
          configuration,
        },
      ];
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveProjectDetailsConfigurationRequested]:
    handleRetrieveProjectDetailsConfigurationRequest,
  [events.retrieveProjectDetailsConfigurationSucceeded]:
    handleRetrieveProjectDetailsConfigurationSuccess,
  [events.retrieveProjectDetailsConfigurationFailed]:
    handleRetrieveProjectDetailsConfigurationFailure,
  [events.editProjectDetailsConfigurationRequested]:
    handleEditProjectDetailsConfigurationRequest,
  [events.editProjectDetailsConfigurationSucceeded]:
    handleEditProjectDetailsConfigurationSuccess,
  [events.editProjectDetailsConfigurationFailed]:
    handleEditProjectDetailsConfigurationFailure,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
